import React, {useMemo, useState} from 'react';
import Page from "components/Page";
import Card from "components/Card";
import StatList from "components/StatList";
import StatBox from "components/StatBox";
import {
  BellIcon,
  CalculatorIcon,
  ChartPieIcon,
  ClockIcon,
  DeviceMobileIcon,
  FastForwardIcon,
  LockOpenIcon,
  MailIcon,
  PlayIcon,
  TicketIcon,
  TruckIcon,
  UserGroupIcon,
  UsersIcon
} from "@heroicons/react/outline";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {EngagementInsightFilters, InsightFilters} from "types/OperatorInsights";
import {dateString, defaultFilters} from 'services/insight';
import InsightControls from "components/InsightControls";
import VisitorsChart from "components/Chart/VisitorsChart";
import DailyVisitsChart from "components/Chart/DailyVisitsChart";
import {Duration} from "luxon";
import VisitDurationChart from "components/Chart/VisitDurationChart";
import VisitsChart from 'components/Chart/VisitsChart';
import SiteSelect from "components/InsightControls/SiteSelect";
import {Site} from "types/Site";
import {useAuth} from "context/AuthContext";
import FeatureFlag from "types/FeatureFlag";
import {UnitSystem} from "types/UnitSystem";
import {useOperator} from "context/OperatorContext";
import convert from "convert";
import ExportButton from "../../../components/ExportButton";
import VisitorsDensityMap from "../../../components/Maps/VisitorsDensityMap";

// Helpers ----

/**
 * Calculate the rate between two values as a percentage
 */
function rate(firstValue: number, secondValue: number): string {
  if (secondValue === 0) {
    return '0%'
  }

  return ((firstValue / secondValue) * 100).toFixed(1) + '%'
}

function formatLength(value: number, system: UnitSystem): string {
  if(value <= 0) return '0';

  const convertedValue = convert(value, 'metres')
    .to('best', system)

  return `${convertedValue.quantity.toFixed(2)}${convertedValue.unit}`;
}

const breadcrumbs = {"Performance": "/", "Engagement": "/insights/engagement"}

// Component ----

function EngagementInsights() {
  const {settings, termFor} = useOperator();
  const {canUse} = useAuth();

  const [filters, setFilters] = useState<EngagementInsightFilters>({...defaultFilters, role: null});
  const [site, setSite] = useState<Site | null>(null);

  const sinceInTimezone = useMemo(() => filters.since?.setZone(site?.timezone ?? 'local', { keepLocalTime: true}), [site, filters.since]);
  const untilInTimezone = useMemo(() => filters.until?.setZone(site?.timezone ?? 'local', { keepLocalTime: true}), [site, filters.until]);

  const {data, isLoading, isFetching} = useQuery(
    ['insights', 'engagement', {...filters, since: sinceInTimezone, until: untilInTimezone}],
    ({signal}) => ngage.insights.engagement({...filters, since: sinceInTimezone, until: untilInTimezone}, {signal}),
    {
      keepPreviousData: true,
      enabled: !!sinceInTimezone && !!untilInTimezone,
    }
  )

  function changeStandardFilters(standardFilters: InsightFilters) {
    setFilters({...filters, ...standardFilters})
  }

  function selectSite(site: Site | null) {
    setSite(site);
    setFilters({...filters, site: site?.id ?? null})
  }

  const sinceDateString = filters.since.toFormat('dd/MM/yyyy');
  const untilDateString = filters.until.toFormat('dd/MM/yyyy');

  const actions = (
    <ExportButton
      getData={() => ngage.insights.engagementExport(filters, 'csv')}
      dataType={'text/csv'}
      prefix={'ngage-engagement-insights'}
      extension={'csv'}
    />
  );

  const enabledPermissionPercentages = data?.summary?.permission_metrics?.overview?.enabled_breakdown?.percentages;

  return (
    <Page
      title="Engagement"
      subTitle='Understand more about engagement with your app users'
      breadcrumbs={breadcrumbs}
      actions={actions}
      isLoading={isLoading}
    >

      {/* Controls */}
      <Card isLoading={isFetching}>
        <InsightControls filters={filters} onChange={changeStandardFilters}>

          {/* Context-specific controls */}
          <SiteSelect id="select-site" name="site" label={`${termFor('Site')}`} onChange={selectSite}/>
        </InsightControls>

      </Card>

      {/* Stats */}
      <div className="mt-4 flex flex-col gap-6">
        <div>
          <h2 className='text-lg font-medium leading-6 text-gray-900 pb-3'>Audience</h2>
          <StatList columns={3}>
            <StatBox
              name="Registered Audience Size"
              icon={UserGroupIcon}
              stat={data?.summary.total_audience_members.toLocaleString()}
              change={data?.summary.new_audience_members.toLocaleString()}
              isLoading={isLoading}
              tooltip={`The number of people that have registered to use your app, as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
            />
            <StatBox
              name="Active Audience Size"
              icon={UsersIcon}
              stat={data?.summary.new_active_audience_members.toLocaleString()}
              isLoading={isLoading}
              tooltip={`The number of people that have actually used your app between ${sinceDateString} and ${untilDateString}.`}
            />
            <StatBox
              name="Audience Activity Rate"
              icon={ChartPieIcon}
              stat={data ? rate(data.summary.new_active_audience_members, data.summary.total_audience_members) : '0%'}
              isLoading={isLoading}
              tooltip={`The proportion of your audience that was active between ${sinceDateString} and ${untilDateString}.`}
            />
            <StatBox
              name="Notifiable Audience Size"
              icon={BellIcon}
              stat={data?.summary.total_notifiable_users.toLocaleString()}
              change={data?.summary.new_notifiable_users.toLocaleString()}
              isLoading={isLoading}
              tooltip={`The number of audience members that can be reached with push notification messages, as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
            />
          </StatList>
        </div>

        <div>
          <h2 className='text-lg font-medium leading-6 text-gray-900 pb-3'>Email Subscription</h2>
          <StatList columns={3}>
            <StatBox
              name="Subscribed Audience Size"
              icon={MailIcon}
              stat={data?.summary.total_subscribed_users.toLocaleString()}
              change={data?.summary.new_subscribed_users.toLocaleString()}
              isLoading={isLoading}
              tooltip={`The number of audience members subscribed to email messages, as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
            />
            <StatBox
              name="Unsubscribed Audience Size"
              icon={MailIcon}
              stat={data?.summary.total_unsubscribed_users.toLocaleString()}
              isLoading={isLoading}
              tooltip={`The number of audience members unsubscribed to email messages, as of ${untilDateString}.`}
            />
            <StatBox
              name="Subscription Rate"
              icon={ChartPieIcon}
              stat={data ? rate(data.summary.total_subscribed_users, data.summary.total_audience_members) : '0%'}
              isLoading={isLoading}
              tooltip={`The proportion of audience members subscribed to email messages, as of ${untilDateString}.`}
            />
            <StatBox
              name="Unsubscribed via app"
              icon={DeviceMobileIcon}
              stat={data?.summary.unsubscribed_via_app.toLocaleString()}
              isLoading={isLoading}
              tooltip={`The number of app users who have unsubscribed in-app between ${sinceDateString} and ${untilDateString}.`}
            />
            <StatBox
              name="Unsubscribed via email"
              icon={MailIcon}
              stat={data?.summary.unsubscribed_via_email.toLocaleString()}
              isLoading={isLoading}
              tooltip={`The number of app users who have unsubscribed using the email link between ${sinceDateString} and ${untilDateString}.`}
            />
          </StatList>
        </div>

        <div>
          <h2 className='text-lg font-medium leading-6 text-gray-900 pb-3'>Visits</h2>
          <StatList columns={3}>
            <StatBox
              name={`${termFor('Visits')}`}
              icon={TruckIcon}
              stat={data?.summary.total_visits.toLocaleString()}
              change={data?.summary.new_visits.toLocaleString()}
              isLoading={isLoading}
              tooltip={`The number of audience ${termFor('visits')} recorded, as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
            />
            <StatBox
              name="Visitors"
              icon={TicketIcon}
              stat={data?.summary.total_visitors.toLocaleString()}
              change={data?.summary.new_visitors.toLocaleString()}
              isLoading={isLoading}
              tooltip={`The number of unique visiting audience members, as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
            />
            <StatBox
              name={`Avg. ${termFor('Visit')} Duration`}
              icon={ClockIcon}
              stat={Duration.fromObject({hours: 0, minutes: 0, seconds: data?.summary.average_visit_duration}).normalize().toFormat("h'h', m'm'")}
              isLoading={isLoading}
              tooltip={`The mean average duration of audience member ${termFor('visits')} between ${sinceDateString} and ${untilDateString}.`}
            />
            <StatBox
              name="Avg. Dwell Duration"
              icon={ClockIcon}
              stat={Duration.fromObject({hours: 0, minutes: 0, seconds: data?.summary.average_dwell_duration}).normalize().toFormat("h'h', m'm', s's'")}
              tooltip={`The mean average dwell duration between ${sinceDateString} and ${untilDateString}.`}
            />
          </StatList>
        </div>

        <div>
          <h2 className='text-lg font-medium leading-6 text-gray-900 pb-3'>Enabled Permissions</h2>
          <StatList columns={3}>
            <StatBox
              name="Location"
              icon={LockOpenIcon}
              stat={`${enabledPermissionPercentages?.location?.toFixed(0) ?? '0'}%`}
              isLoading={isLoading}
              tooltip={`The percentage of people that have granted location permission.`}
            />
            <StatBox
              name="Notifications"
              icon={LockOpenIcon}
              stat={`${enabledPermissionPercentages?.notifications?.toFixed(0) ?? '0'}%`}
              isLoading={isLoading}
              tooltip={`The percentage of people that have granted notifications permission.`}
            />
            <StatBox
              name="Bluetooth"
              icon={LockOpenIcon}
              stat={`${enabledPermissionPercentages?.bluetooth?.toFixed(0) ?? '0'}%`}
              isLoading={isLoading}
              tooltip={`The percentage of people that have granted bluetooth permission.`}
            />
            <StatBox
              name="Camera"
              icon={LockOpenIcon}
              stat={`${enabledPermissionPercentages?.camera?.toFixed(0) ?? '0'}%`}
              isLoading={isLoading}
              tooltip={`The percentage of people that have granted camera permission.`}
            />
            <StatBox
              name="Media"
              icon={LockOpenIcon}
              stat={`${enabledPermissionPercentages?.media?.toFixed(0) ?? '0'}%`}
              isLoading={isLoading}
              tooltip={`The percentage of people that have granted media permission.`}
            />
            {/*<StatBox*/}
            {/*  name="Microphone"*/}
            {/*  icon={LockOpenIcon}*/}
            {/*  stat={`${enabledPermissionPercentages?.microphone?.toFixed(0) ?? '0'}%`}*/}
            {/*  isLoading={isLoading}*/}
            {/*  tooltip={`The percentage of people that have granted microphone permission.`}*/}
            {/*/>*/}
            <StatBox
              name="Background Location"
              icon={LockOpenIcon}
              stat={`${enabledPermissionPercentages?.background_location?.toFixed(0) ?? '0'}%`}
              isLoading={isLoading}
              tooltip={`The percentage of people that have granted background location permission.`}
            />
            {canUse(FeatureFlag.Activities) &&
              <StatBox
                name="Pedometer"
                icon={LockOpenIcon}
                stat={`${enabledPermissionPercentages?.pedometer?.toFixed(0) ?? '0'}%`}
                isLoading={isLoading}
                tooltip={`The percentage of people that have granted pedometer permission.`}
              />
            }
          </StatList>
        </div>

        {canUse(FeatureFlag.Activities) &&
          <div>
            <h2 className='text-lg font-medium leading-6 text-gray-900 pb-3'>Activities</h2>
            <StatList columns={3}>
              <StatBox
                name="Activities"
                icon={PlayIcon}
                isLoading={isLoading}
                stat={(data?.summary.total_activities ?? 0).toLocaleString()}
                change={(data?.summary.new_activities ?? 0).toLocaleString()}
                tooltip={`The number of activities recorded, as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
              />
              <StatBox
                name="Activity Steps"
                icon={FastForwardIcon}
                isLoading={isLoading}
                stat={(data?.summary.total_steps ?? 0).toLocaleString()}
                change={(data?.summary.new_steps ?? 0).toLocaleString()}
                tooltip={`The number of steps recorded by activity participants, as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
              />
              <StatBox
                name="Activity Distance"
                icon={CalculatorIcon}
                stat={formatLength(
                  data?.summary.total_distance ?? 0,
                  settings?.localisation.length_units ?? UnitSystem.METRIC
                )}
                change={formatLength(
                  data?.summary.new_distance ?? 0,
                  settings?.localisation.length_units ?? UnitSystem.METRIC
                )}
                tooltip={`The distance travelled by activity participants, as of ${untilDateString}. Also shown is the change since ${sinceDateString}.`}
              />
            </StatList>
          </div>
        }
      </div>

      {/* Charts */}

      <h2 className='text-lg font-medium leading-6 text-gray-900 mt-6'>Charts</h2>
      <div className="mt-4 grid gap-4 grid-cols-1 lg:grid-cols-2">
        <Card
          heading={`${termFor('Visits')}`}
          subheading={`Total ${termFor('visits')} per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          {data && <VisitsChart data={data} interval={filters.interval} compare={filters.compare}/>}
        </Card>

        <Card
          heading="Visitors"
          subheading={`Total visitors per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          {data && <VisitorsChart data={data} interval={filters.interval} compare={filters.compare}/>}
        </Card>

        <Card
          heading={`Total Daily ${termFor('Visits')}`}
          subheading={`All ${termFor('visits')} per weekday as of ${dateString(filters.until, filters.interval)}`}
        >
          <div className="h-96">
            {data && <DailyVisitsChart data={data} total/>}
          </div>
        </Card>

        <Card
          heading={`Daily ${termFor('Visits')}`}
          subheading={`${termFor('Visits')} per weekday between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          <div className="h-96">
            {data && <DailyVisitsChart data={data}/>}
          </div>
        </Card>

        <Card
          heading={`Avg. ${termFor('Visit')} Duration`}
          subheading={`Average ${termFor('visits')} duration per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}
        >
          {data && <VisitDurationChart data={data} interval={filters.interval} compare={filters.compare}/>}
        </Card>

        <VisitorsDensityMap
          height={"32rem"}
          since={filters.since}
          until={filters.until}
          siteId={site?.id}
        />

        {/*<Card*/}
        {/*  heading="Avg. Dwell Time"*/}
        {/*  subheading={`Average dwell time per ${filters.interval} between ${dateString(filters.since, filters.interval)} and ${dateString(filters.until, filters.interval)}`}*/}
        {/*>*/}
        {/*  {data && <DwellTimeChart data={data} interval={filters.interval} compare={filters.compare}/>}*/}
        {/*</Card>*/}
      </div>
    </Page>
  );
}

export default EngagementInsights;